import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { describeError } from '@pc/core';
import { attachLabelToShipment, computePresetChargeCents } from '@pc/db';
import {
  Button,
  Field,
  Input,
  READ_ONLY_TOOLTIP,
  Rule,
  SmallCap,
  useCanWriteOrg,
  useIsOrgAdmin,
} from '@pc/ui';
import type {
  Carrier,
  CarrierAccount,
  LabelStatus,
  ShippingLabel,
  ShippingPackagePreset,
  ShippingRate,
  ShippingShipment,
  ShippingShipmentAddress,
} from '@pc/db';
import {
  useCarrierAccounts,
  useCreatePackagePreset,
  useLabelsForOrder,
  usePackagePresets,
  usePurchaseShippingLabel,
  useQuoteShipping,
  useRefundShippingLabel,
  useStoreShippingSettings,
} from '../queries';
import { useActiveCtx } from '../useCtx';

const CARRIER_LABELS: Record<Carrier, string> = {
  shippo: 'Shippo',
  easypost: 'EasyPost',
  ups: 'UPS',
  pirateship: 'PirateShip',
};

const STATUS_LABELS: Record<LabelStatus, string> = {
  purchased: 'Purchased',
  refund_pending: 'Refund pending',
  refunded: 'Refunded',
  voided: 'Voided',
  failed: 'Failed',
};

// Status pill colours mirror the @pc/ui semantic palette. Stick to
// tokens (no hex) so theme tweaks stay centralised.
const STATUS_CLASSES: Record<LabelStatus, string> = {
  purchased: 'border-olive text-olive bg-olive/10',
  refund_pending: 'border-rule text-ink-2 bg-paper-2',
  refunded: 'border-rule text-muted bg-paper-2',
  voided: 'border-rule text-muted bg-paper-2',
  failed: 'border-clay text-clay bg-clay/10',
};

function StatusPill({ status }: { status: LabelStatus }) {
  return (
    <span
      className={`px-1.5 py-0.5 border text-xs ${
        STATUS_CLASSES[status] ?? 'border-rule text-ink-2'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function isImageLabel(url: string | null): boolean {
  if (!url) return false;
  return /\.(png|jpg|jpeg|gif|webp)(\?|$)/i.test(url);
}

/**
 * Inline label preview. We request 4x6 labels from Shippo (see
 * supabase/functions/_shared/shipping/shippo.ts), so the asset is a
 * landscape PDF roughly 4 inches wide by 6 inches tall. Modern browsers
 * render single-page PDFs inline via <object>; we constrain the box to
 * keep the layout dense.
 *
 * If the carrier ever returns a PNG (some adapters do), we render it as
 * an <img> so it can be cached and zoomed natively.
 */
function LabelPreview({ url, status }: { url: string; status: LabelStatus }) {
  const muted = status !== 'purchased';
  return (
    <div
      className={`border border-rule bg-paper ${
        muted ? 'opacity-60' : ''
      }`}
      style={{ width: '100%', maxWidth: 320 }}
    >
      {isImageLabel(url) ? (
        <img
          src={url}
          alt="Shipping label"
          loading="lazy"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      ) : (
        <object
          data={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
          type="application/pdf"
          aria-label="Shipping label preview"
          style={{ width: '100%', height: 240, display: 'block' }}
        >
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="block p-3 text-sm underline"
          >
            Open label PDF
          </a>
        </object>
      )}
    </div>
  );
}

interface Props {
  orderId: string;
  /**
   * Snapshot of the order's shipping_address_snapshot. Most fields are
   * carried inline -- we trust this snapshot rather than re-deriving from
   * the live wholesaler_recipients row.
   */
  shipTo: ShippingShipmentAddress;
  /** When set, the purchased label is attached to this fulfillment shipment. */
  shipmentId?: string;
  /** `shipment` hides the order-level label history and uses tighter copy. */
  variant?: 'order' | 'shipment';
}

const REQUIRED_ADDRESS_FIELDS = ['name', 'street1', 'city', 'state', 'zip', 'country'] as const;

function missingFields(addr: Partial<ShippingShipmentAddress>): string[] {
  return REQUIRED_ADDRESS_FIELDS.filter((f) => !((addr as Record<string, string | undefined>)[f] ?? '').trim?.());
}

export function ShippingLabelPanel({
  orderId,
  shipTo,
  shipmentId,
  variant = shipmentId ? 'shipment' : 'order',
}: Props) {
  const ctx = useActiveCtx();
  const qc = useQueryClient();
  const canWrite = useCanWriteOrg();
  const isShipmentContext = variant === 'shipment' || Boolean(shipmentId);
  const isAdmin = useIsOrgAdmin();
  const accountsQ = useCarrierAccounts(ctx);
  const settingsQ = useStoreShippingSettings(ctx);
  const labelsQ = useLabelsForOrder(ctx, orderId);
  const quote = useQuoteShipping(ctx);
  const purchase = usePurchaseShippingLabel(ctx);
  const refund = useRefundShippingLabel(ctx);
  const [refundingId, setRefundingId] = useState<string | null>(null);

  const activeAccounts: CarrierAccount[] = (accountsQ.data ?? []).filter(
    (a) => a.is_active && !a.archived_at,
  );

  // Pull warehouse origin + default parcel from per-store settings. The
  // operator sets these once on the Shipping settings page.
  const shipFrom: ShippingShipmentAddress = useMemo(() => {
    const f = settingsQ.data?.ship_from ?? {};
    return {
      name: (f.name as string) ?? '',
      company: (f.company as string) ?? undefined,
      street1: (f.street1 as string) ?? '',
      street2: (f.street2 as string) ?? undefined,
      city: (f.city as string) ?? '',
      state: (f.state as string) ?? '',
      zip: (f.zip as string) ?? '',
      country: (f.country as string) ?? 'US',
      email: (f.email as string) ?? undefined,
      phone: (f.phone as string) ?? undefined,
    };
  }, [settingsQ.data]);

  const fromMissing = missingFields(shipFrom);
  const toMissing = missingFields(shipTo);

  const [carrierAccountId, setCarrierAccountId] = useState<string>('');
  const initialParcel = settingsQ.data?.default_parcel;
  const [length, setLength] = useState(String(initialParcel?.length_in ?? '12'));
  const [width, setWidth] = useState(String(initialParcel?.width_in ?? '9'));
  const [height, setHeight] = useState(String(initialParcel?.height_in ?? '3'));
  const [weight, setWeight] = useState(String(initialParcel?.weight_oz ?? '16'));
  const [rates, setRates] = useState<ShippingRate[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Package presets (OfficeHub parity): applying one fills the parcel
  // fields AND attaches its markup recipe to the eventual purchase --
  // the order gets charged the marked-up client price, not the raw
  // carrier cost.
  const presetsQ = usePackagePresets(ctx);
  const createPreset = useCreatePackagePreset(ctx);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const presets = presetsQ.data ?? [];
  const selectedPreset = presets.find((p) => p.id === selectedPresetId) ?? null;

  function applyPreset(p: ShippingPackagePreset) {
    if (selectedPresetId === p.id) {
      // Toggle off -> back to a custom parcel (no markup recipe).
      setSelectedPresetId(null);
      return;
    }
    setLength(String(Number(p.length_in)));
    setWidth(String(Number(p.width_in)));
    setHeight(String(Number(p.height_in)));
    setWeight(String(Number(p.weight_oz)));
    setSelectedPresetId(p.id);
    // Old rates were quoted for the previous parcel; drop them so the
    // operator re-quotes with the preset's dimensions.
    setRates(null);
  }

  async function onSavePreset() {
    const name = presetName.trim();
    if (!name) return;
    setErr(null);
    try {
      const created = await createPreset.mutateAsync({
        name,
        lengthIn: parseFloat(length || '0') || 0,
        widthIn: parseFloat(width || '0') || 0,
        heightIn: parseFloat(height || '0') || 0,
        weightOz: parseFloat(weight || '0') || 0,
      });
      setSelectedPresetId(created.id);
      setPresetName('');
      setShowSavePreset(false);
    } catch (e) {
      setErr(describeError(e));
    }
  }

  function presetMarkupHint(p: ShippingPackagePreset): string | null {
    const pct = Number(p.rate_markup_percent) || 0;
    const flat = Number(p.packing_markup_cents) || 0;
    const parts: string[] = [];
    if (pct > 0) parts.push(`+${pct}%`);
    if (flat > 0) parts.push(`+$${(flat / 100).toFixed(2)} packing`);
    return parts.length > 0 ? parts.join(' ') : null;
  }

  function buildShipment(): ShippingShipment {
    return {
      fromAddress: shipFrom,
      toAddress: shipTo,
      parcel: {
        length_in: parseFloat(length || '0') || 0,
        width_in: parseFloat(width || '0') || 0,
        height_in: parseFloat(height || '0') || 0,
        weight_oz: parseFloat(weight || '0') || 0,
      },
    };
  }

  async function onQuote() {
    setErr(null);
    setRates(null);
    if (!carrierAccountId) {
      setErr('Pick a carrier account first.');
      return;
    }
    if (fromMissing.length > 0) {
      setErr(
        `Your ship-from address is missing: ${fromMissing.join(', ')}. Set it on the Shipping settings page.`,
      );
      return;
    }
    if (toMissing.length > 0) {
      setErr(`The order's ship-to address is missing: ${toMissing.join(', ')}.`);
      return;
    }
    try {
      const result = await quote.mutateAsync({
        carrierAccountId,
        shipment: buildShipment(),
      });
      setRates(result);
    } catch (e) {
      setErr(describeError(e));
    }
  }

  async function onPurchase(rate: ShippingRate) {
    setErr(null);
    try {
      const result = await purchase.mutateAsync({
        carrierAccountId,
        orderId,
        shipment: buildShipment(),
        rateId: rate.id,
        rateCents: rate.amountCents,
        rateCurrency: rate.currency,
        // Marked-up client price (rate x preset % + flat packing fee).
        // This is what rolls into the order's shipping_cents.
        chargedCents: computePresetChargeCents(rate.amountCents, selectedPreset),
        packagePresetId: selectedPreset?.id ?? null,
      });
      if (shipmentId) {
        await attachLabelToShipment(ctx, {
          shipmentId,
          labelId: result.label.id,
        });
        qc.invalidateQueries({ queryKey: ['fulfillment'] });
      }
      setRates(null);
    } catch (e) {
      setErr(describeError(e));
    }
  }

  async function onRefund(label: ShippingLabel) {
    setErr(null);
    if (!label.carrier_account_id) {
      setErr('This label has no carrier account on file and cannot be refunded.');
      return;
    }
    const confirmed = typeof window !== 'undefined'
      ? window.confirm(
          'Request a refund from the carrier? The label will be cancelled. Carriers may take a few minutes to confirm the refund (USPS resolves fast; UPS / FedEx may stay pending).',
        )
      : true;
    if (!confirmed) return;
    setRefundingId(label.id);
    try {
      await refund.mutateAsync({
        carrierAccountId: label.carrier_account_id,
        labelId: label.id,
      });
    } catch (e) {
      setErr(describeError(e));
    } finally {
      setRefundingId(null);
    }
  }

  const sectionTitle = isShipmentContext ? 'Buy label (Shippo)' : 'Shipping label';

  return (
    <div>
      <SmallCap>{sectionTitle}</SmallCap>

      {!isShipmentContext && (labelsQ.data?.length ?? 0) > 0 ? (
        <ul className="flex flex-col mt-3 gap-3">
          {labelsQ.data!.map((l, i) => {
            const canRefund = l.status === 'purchased' && !!l.carrier_account_id;
            const refundInFlight = refundingId === l.id && refund.isPending;
            return (
              <li key={l.id} className="flex flex-col gap-3">
                {i > 0 ? <Rule className="my-1" /> : null}
                <div className="flex items-start justify-between gap-4 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <StatusPill status={l.status as LabelStatus} />
                      <span className="text-xs text-muted">
                        {CARRIER_LABELS[l.carrier]}
                      </span>
                      {l.rate_cents > 0 ? (
                        <span className="text-xs tabular text-ink-2">
                          ${(Number(l.rate_cents) / 100).toFixed(2)} {l.currency}
                        </span>
                      ) : null}
                      {l.charged_cents != null && Number(l.charged_cents) !== Number(l.rate_cents) ? (
                        <span className="text-xs tabular text-olive">
                          client ${(Number(l.charged_cents) / 100).toFixed(2)}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 tabular text-sm">
                      {l.tracking_number ?? '(no tracking)'}
                    </div>
                    <div className="text-xs text-muted truncate">
                      {l.service_level}
                    </div>
                    {l.refund_status ? (
                      <div className="mt-1 text-xs text-muted">
                        Carrier refund status:{' '}
                        <span className="tabular">{l.refund_status}</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-1 items-end text-xs">
                    {l.label_url ? (
                      <a
                        href={l.label_url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-clay"
                      >
                        Open / print label
                      </a>
                    ) : null}
                    {l.tracking_url ? (
                      <a
                        href={l.tracking_url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-clay"
                      >
                        Track package
                      </a>
                    ) : null}
                    {canRefund ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onRefund(l)}
                        disabled={!canWrite || refund.isPending}
                        title={canWrite ? undefined : READ_ONLY_TOOLTIP}
                      >
                        {refundInFlight ? 'Requesting...' : 'Request refund'}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {l.label_url ? (
                  <LabelPreview url={l.label_url} status={l.status as LabelStatus} />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : activeAccounts.length === 0 ? (
        <div className="text-sm text-ink-2 mt-2">
          <p>
            The shipping module is enabled but no carriers are connected yet.
          </p>
          {isAdmin ? (
            <div className="mt-3">
              <Link to="/shipping">
                <Button size="sm">Connect a carrier</Button>
              </Link>
            </div>
          ) : (
            <p className="text-xs text-muted mt-2">
              Ask an admin to connect a carrier from the Shipping page.
            </p>
          )}
        </div>
      ) : fromMissing.length > 0 ? (
        <div className="text-sm text-ink-2 mt-2">
          <p>
            Set your default ship-from address before quoting. Without a
            valid warehouse origin, the carrier returns zero rates.
          </p>
          <p className="text-xs text-muted mt-1">
            Missing: {fromMissing.join(', ')}
          </p>
          {isAdmin ? (
            <div className="mt-3">
              <Link to="/shipping">
                <Button size="sm">Open shipping settings</Button>
              </Link>
            </div>
          ) : (
            <p className="text-xs text-muted mt-2">
              Ask an admin to set the ship-from address in Shipping settings.
            </p>
          )}
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-3 text-sm">
          <Field label="Carrier">
            <select
              value={carrierAccountId}
              onChange={(e) => setCarrierAccountId(e.target.value)}
              className="bg-paper border border-rule-strong rounded-none px-3 py-2 text-sm text-ink"
            >
              <option value="">Pick a carrier...</option>
              {activeAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} - {CARRIER_LABELS[a.carrier]}
                </option>
              ))}
            </select>
          </Field>
          {presets.length > 0 ? (
            <div>
              <div className="text-xs text-muted mb-1.5">Package</div>
              <div className="flex flex-wrap gap-1.5">
                {presets.map((p) => {
                  const active = selectedPresetId === p.id;
                  const hint = presetMarkupHint(p);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyPreset(p)}
                      title={hint ? `Client price: rate ${hint}` : undefined}
                      className={`px-2 py-1 border text-xs transition-colors ${
                        active
                          ? 'border-ink bg-ink text-paper'
                          : 'border-rule-strong bg-paper text-ink hover:border-ink'
                      }`}
                    >
                      {p.name}{' '}
                      <span className={active ? 'opacity-70' : 'text-muted'}>
                        {Number(p.length_in)}x{Number(p.width_in)}x{Number(p.height_in)}in · {Number(p.weight_oz)}oz
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          <div className="grid grid-cols-4 gap-2">
            <Field label="L (in)">
              <Input inputMode="decimal" value={length} onChange={(e) => setLength(e.target.value)} />
            </Field>
            <Field label="W (in)">
              <Input inputMode="decimal" value={width} onChange={(e) => setWidth(e.target.value)} />
            </Field>
            <Field label="H (in)">
              <Input inputMode="decimal" value={height} onChange={(e) => setHeight(e.target.value)} />
            </Field>
            <Field label="Weight (oz)">
              <Input inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} />
            </Field>
          </div>
          {selectedPreset && presetMarkupHint(selectedPreset) ? (
            <p className="text-xs text-muted -mt-1">
              Client shipping price = carrier rate {presetMarkupHint(selectedPreset)}.
              The order is charged the client price; the label cost stays on record.
            </p>
          ) : null}
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={onQuote}
              disabled={!canWrite || quote.isPending}
              title={canWrite ? undefined : READ_ONLY_TOOLTIP}
            >
              {quote.isPending ? 'Getting rates...' : 'Get rates'}
            </Button>
            {canWrite ? (
              showSavePreset ? (
                <span className="flex items-center gap-1.5">
                  <Input
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    placeholder="Preset name"
                    className="!py-1 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void onSavePreset();
                    }}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onSavePreset}
                    disabled={createPreset.isPending || !presetName.trim()}
                  >
                    {createPreset.isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowSavePreset(false)}>
                    Cancel
                  </Button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSavePreset(true)}
                  className="text-xs text-muted underline hover:text-ink"
                >
                  Save as preset
                </button>
              )
            ) : null}
          </div>

          {rates === null ? null : rates.length === 0 ? (
            <p className="text-sm text-muted mt-2">
              The carrier returned no rates. Double-check the ship-to address,
              dimensions, and weight, then try again.
            </p>
          ) : (
            <ul className="flex flex-col mt-2">
              {rates.map((r, i) => {
                const charged = computePresetChargeCents(r.amountCents, selectedPreset);
                const hasMarkup = charged !== r.amountCents;
                return (
                  <li key={r.id}>
                    {i > 0 ? <Rule className="my-2" /> : null}
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div>{r.service}</div>
                        <div className="text-xs text-muted">
                          {r.estimatedDays ? `est. ${r.estimatedDays}d` : '-'}
                        </div>
                      </div>
                      <div className="tabular text-right">
                        <div>
                          ${(charged / 100).toFixed(2)} {r.currency}
                        </div>
                        {hasMarkup ? (
                          <div className="text-xs text-muted">
                            cost ${(r.amountCents / 100).toFixed(2)}
                          </div>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        disabled={!canWrite || purchase.isPending}
                        title={canWrite ? undefined : READ_ONLY_TOOLTIP}
                        onClick={() => onPurchase(r)}
                      >
                        {purchase.isPending ? 'Buying...' : 'Buy label'}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {err ? <p className="text-sm text-clay mt-3">{err}</p> : null}
    </div>
  );
}
